export type RateCode = 'professionalRate' | 'baseMargin' | 'baseChargeRate' | 'chargeRate';
export type RateValues = Record<RateCode, number>;
export type RateVersion =
  | 'professionalRateAndBaseMargin'
  | 'professionalRateAndBaseChargeRate'
  | 'professionalRateAndChargeRate'
  | 'baseMarginAndBaseChargeRate'
  | 'baseMarginAndChargeRate';

export const CONTROLLABLE_CODES: RateCode[] = [
  'professionalRate',
  'baseMargin',
  'baseChargeRate',
  'chargeRate',
];
export const DEFAULT_CONTROLLED: RateCode[] = ['baseMargin', 'chargeRate'];
export const CONTROLLED_RATE_FIELDS_KEY = 'controlled_rate_fields';
// Client-only flag: set once the admin actually interacts with a tab's rate fields
// (typing or toggling). The transform uses it to drop controller-computed snapshots
// of inherited values for tabs the admin never touched; stripped from the payload.
export const RATE_CONTROLLER_ENGAGED_KEY = 'rate_controller_engaged';

// Union of every field recompute() may write across the three tabs. When a tab
// disengages (the admin reverts all of their input) these are reset to their form
// defaults so controller snapshots of inherited values are not submitted as own.
export const CONTROLLER_WRITTEN_FIELDS = [
  'pro_rate_cents',
  'base_charge_rate_cents',
  'charge_rate_cents',
  'base_margin_percent',
  'base_margin_percent_w2',
  'base_margin_percent_w2_travel',
  'margin_percent_1099',
  'total_margin_percent_w2',
  'margin_percent_w2_travel',
  'margin_percent_w2_tcv',
  'blended_rate_cents',
  'invoicing_markup',
  'misc_markup',
  'operational_markup',
];

const pairKey = (firstCode: RateCode, secondCode: RateCode) =>
  [firstCode, secondCode].sort().join('|');

const EXCLUDED_PAIR = pairKey('baseChargeRate', 'chargeRate');

const VERSION_MAP: Record<string, RateVersion> = {
  [pairKey('professionalRate', 'baseMargin')]: 'professionalRateAndBaseMargin',
  [pairKey('professionalRate', 'baseChargeRate')]: 'professionalRateAndBaseChargeRate',
  [pairKey('professionalRate', 'chargeRate')]: 'professionalRateAndChargeRate',
  [pairKey('baseMargin', 'baseChargeRate')]: 'baseMarginAndBaseChargeRate',
  [pairKey('baseMargin', 'chargeRate')]: 'baseMarginAndChargeRate',
};

export const versionOf = (controlled: RateCode[]): RateVersion | null => {
  if (controlled.length !== 2) {
    return null;
  }
  return VERSION_MAP[pairKey(controlled[0], controlled[1])] ?? null;
};

export type ChargeRateMarkups = {
  invoicingMarkup?: number;
  miscMarkup?: number;
  operationalMarkup?: number;
};

export const markupFactorOf = ({
  invoicingMarkup = 0,
  miscMarkup = 0,
  operationalMarkup = 0,
}: ChargeRateMarkups): number =>
  (1 + (Number(invoicingMarkup) || 0)) *
  (1 + (Number(miscMarkup) || 0)) *
  (1 + (Number(operationalMarkup) || 0));

export const solveRates = ({
  controlled,
  values,
  markups,
  blendedFactor = 1,
  burdenDivisor = 1,
}: {
  controlled: RateCode[];
  values: RateValues;
  markups: ChargeRateMarkups;
  blendedFactor?: number;
  // W2 Local folds employer burdens into the effective margin, so the professional
  // rate is discounted by (1 + burdens). Defaults to 1 (no burdens) for ten99/travel.
  burdenDivisor?: number;
}): RateValues => {
  const markupFactor = markupFactorOf(markups);
  const factor = Number(blendedFactor) > 0 ? Number(blendedFactor) : 1;
  const burden = Number(burdenDivisor) > 0 ? Number(burdenDivisor) : 1;
  let { professionalRate, baseMargin, baseChargeRate, chargeRate } = values;

  switch (versionOf(controlled)) {
    case 'professionalRateAndBaseMargin':
      baseChargeRate = (professionalRate * burden) / ((1 - baseMargin) * factor);
      chargeRate = baseChargeRate * markupFactor;
      break;
    case 'professionalRateAndBaseChargeRate':
      baseMargin = 1 - (professionalRate * burden) / (baseChargeRate * factor);
      chargeRate = baseChargeRate * markupFactor;
      break;
    case 'professionalRateAndChargeRate':
      baseChargeRate = chargeRate / markupFactor;
      baseMargin = 1 - (professionalRate * burden) / (baseChargeRate * factor);
      break;
    case 'baseMarginAndBaseChargeRate':
      professionalRate = (baseChargeRate * factor * (1 - baseMargin)) / burden;
      chargeRate = baseChargeRate * markupFactor;
      break;
    case 'baseMarginAndChargeRate':
      baseChargeRate = chargeRate / markupFactor;
      professionalRate = (baseChargeRate * factor * (1 - baseMargin)) / burden;
      break;
    default:
      break;
  }

  return { professionalRate, baseMargin, baseChargeRate, chargeRate };
};

// Keep exactly 2 controlled, never baseChargeRate+chargeRate together: drop the
// oldest unless that would leave the forbidden pair.
export const swapControlled = (controlled: RateCode[], clicked: RateCode): RateCode[] => {
  if (controlled.includes(clicked)) {
    return controlled;
  }
  const [oldest, newest] = controlled;
  // Drop the oldest and add clicked...
  if (pairKey(newest, clicked) !== EXCLUDED_PAIR) {
    return [newest, clicked];
  }
  // ...unless that would create the forbidden pair — then drop the newest.
  return [oldest, clicked];
};

export const deriveInheritedRatePreview = ({
  inherited,
  source,
  isLocationLevel,
}: {
  inherited: any;
  source: string;
  isLocationLevel: boolean;
}): {
  professionalRate: number | null;
  baseChargeRate: number | null;
  blendedRateCents: number | null;
} => {
  const num = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const present = (v: unknown): boolean =>
    v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

  const isW2 = source.includes('w2');
  const multiplier = markupFactorOf({
    invoicingMarkup: num(inherited?.invoicing_markup),
    miscMarkup: num(inherited?.misc_markup),
    operationalMarkup: isW2 ? num(inherited?.operational_markup) : 0,
  });
  const rawFactor =
    inherited?.use_blended_rate && !isLocationLevel
      ? 1 + num(inherited?.blended_rates?.blended_rate_markup)
      : 1;
  const factor = rawFactor > 0 ? rawFactor : 1;

  const chargeRate = present(inherited?.charge_rate_cents)
    ? Number(inherited.charge_rate_cents)
    : null;
  const baseChargeRate = present(inherited?.base_charge_rate_cents)
    ? Number(inherited.base_charge_rate_cents)
    : chargeRate !== null && multiplier > 0
    ? chargeRate / multiplier
    : null;

  // The resolved settings never carry a pro rate (apply_markups only uses it as an
  // intermediate), so it is always re-derived. The final margin is exact for every
  // classification: the DB defines it as 1 − P/C on the resolved values, with W2
  // burdens (w2.local) and charge markups already folded in.
  const isW2Local = source.includes('w2.local');
  const baseMargin = inherited?.[baseMarginField(source).split('.').pop() as string];
  const finalMargin = source.includes('w2.travel')
    ? inherited?.margin_percent_w2_travel
    : isW2Local
    ? inherited?.total_margin_percent_w2
    : inherited?.margin_percent_1099;

  let professionalRate: number | null = null;
  if (present(finalMargin) && chargeRate !== null) {
    professionalRate = chargeRate * factor * (1 - Number(finalMargin));
  } else if (present(baseMargin) && baseChargeRate !== null && !isW2Local) {
    // Not valid on w2.local: the base margin lacks the employer-burden division,
    // and the burden multipliers live outside the classification hash.
    professionalRate = baseChargeRate * factor * (1 - Number(baseMargin));
  }

  const blendedMarkup = inherited?.blended_rates?.blended_rate_markup;
  const blendedRateCents = present(inherited?.charge_blended_rate_cents)
    ? Number(inherited.charge_blended_rate_cents)
    : chargeRate !== null && present(blendedMarkup)
    ? chargeRate * (1 + Number(blendedMarkup))
    : null;

  return { professionalRate, baseChargeRate, blendedRateCents };
};

export const baseMarginField = (source: string): string => {
  if (source.includes('w2.local')) {
    return `${source}.base_margin_percent_w2`;
  }
  if (source.includes('w2.travel')) {
    return `${source}.base_margin_percent_w2_travel`;
  }
  return `${source}.base_margin_percent`;
};

export const codeToField = (code: RateCode, source: string): string => {
  switch (code) {
    case 'professionalRate':
      return `${source}.pro_rate_cents`;
    case 'baseChargeRate':
      return `${source}.base_charge_rate_cents`;
    case 'chargeRate':
      return `${source}.charge_rate_cents`;
    case 'baseMargin':
      return baseMarginField(source);
  }
};

// Editing the 12hr Charge Rate (blended_rate_cents) is modeled as an update to
// blended_rate_markup (the source of truth). Returns null for an unusable charge
// base or a negative implied markup (caller should not write in those cases).
export const deriveBlendedMarkup = (blendedCents: number, chargeCents: number): number | null => {
  if (!Number.isFinite(chargeCents) || chargeCents <= 0) {
    return null;
  }
  const markup = Math.round((blendedCents / chargeCents - 1) * 10000) / 10000;
  return markup >= 0 ? markup : null;
};
